import { IKey } from '@/common/constants/ikey'
import {
  extractTypedFieldFromListboxRow,
  findListboxRowMatchingSelectedValue,
  parseComboBoxIdFieldPairsFromElement,
  resolveListboxSelectionTypedValue,
} from '@/common/utils/comboBoxSwingFields'
import { toMultiSelectTypedValue } from '@/common/utils/normalizeMultiselectTableValue'
import {
  isTreeTableElementType,
  parseTreeTableRuntimeColumnsFromPage,
  rebuildTreeTableGetDataForWs,
  type TreeTableGetDataColumn,
} from '@/common/utils/treeTableGetData'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import {
  fileItemsToUniDataTable,
  uniDataTableToFileItems,
} from '@/common/utils/fileAttachmentsUniData'
import {
  isLikelyListboxFullItemsArray,
  isLikelyListboxItemsStringDump,
  isLikelyListboxTablePayload,
} from '@/common/utils/listboxScalarSanitize'
import { normalizeFormElementType } from '@/common/utils/formElementType'
import { setType } from '@/common/utils/typedValueSerializer'

export interface NormalizeElementCollectedDataOptions {
  elementName: string
  elTypeNorm: string
  registered?: unknown
  fromElementSlot?: Record<string, unknown>
  pageMerged?: Record<string, unknown>
  fileAttachmentsFromHook?: boolean
}

const unwrapTypedSlotValue = (v: unknown): unknown => {
  if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v && 'type' in v) {
    return (v as { value: unknown }).value
  }
  return v
}

/** DD.MM.YYYY для wire DateRange; Date-like (в т.ч. Vue proxy) через getTime/getFullYear. */
export const formatDateRangePartForGetData = (d: unknown): string => {
  if (d == null || d === '') return ''
  if (typeof d === 'object' && d !== null && typeof (d as { getTime?: unknown }).getTime === 'function') {
    const time = (d as Date).getTime()
    if (typeof time === 'number' && !Number.isNaN(time)) {
      const date = d instanceof Date ? d : new Date(time)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}`
    }
  }
  if (typeof d === 'string') {
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) return d
    const parsed = new Date(d)
    if (!Number.isNaN(parsed.getTime())) {
      return formatDateRangePartForGetData(parsed)
    }
    return d
  }
  return ''
}

/** UI `{from,to}` → Swing `__first`/`__second` строки для pagesData / setValue. */
export function buildDateRangeWireFieldsFromUiValue(
  fieldName: string,
  raw: unknown,
): { firstKey: string; secondKey: string; first: string; second: string } {
  const name = String(fieldName ?? '').trim()
  const dr = parseDaterangePlainForGetData(raw)
  return {
    firstKey: `${name}__first`,
    secondKey: `${name}__second`,
    first: formatDateRangePartForGetData(dr.from),
    second: formatDateRangePartForGetData(dr.to),
  }
}

const parseDaterangePlainForGetData = (raw: unknown): { from: unknown; to: unknown } => {
  const plain = unwrapTypedSlotValue(raw)
  if (plain === '' || plain == null) {
    return { from: null, to: null }
  }
  if (typeof plain === 'object' && !Array.isArray(plain) && plain !== null) {
    if ('from' in plain || 'to' in plain) {
      return {
        from: (plain as { from?: unknown }).from ?? null,
        to: (plain as { to?: unknown }).to ?? null,
      }
    }
  }
  return { from: null, to: null }
}

const unwrapListboxComboSelectedValueForGetData = (raw: unknown): unknown => {
  const v = unwrapTypedSlotValue(raw)
  if (v == null) return ''
  if (isLikelyListboxItemsStringDump(v) || isLikelyListboxFullItemsArray(v) || isLikelyListboxTablePayload(v)) {
    return ''
  }
  if (typeof v === 'object' && !Array.isArray(v) && v !== null && 'value' in v) {
    return unwrapListboxComboSelectedValueForGetData((v as { value: unknown }).value)
  }
  if (typeof v === 'object' && !Array.isArray(v) && v !== null) {
    const o = v as Record<string, unknown>
    if (o.code != null || o._id != null) {
      return o.code ?? o._id ?? ''
    }
  }
  return v
}

const isCheckboxLikeSlotModel = (plain: unknown): boolean => {
  if (plain === null || plain === undefined) return false
  if (typeof plain === 'boolean') return true
  if (typeof plain === 'object' && !Array.isArray(plain) && Array.isArray((plain as { rows?: unknown }).rows)) {
    return true
  }
  return false
}

const checkboxCellToTyped = (val: unknown, preferBoolean: boolean): { type: string; value: unknown } => {
  if (preferBoolean && typeof val === 'boolean') {
    return { type: 'Boolean', value: val }
  }
  if (val === null || val === undefined) {
    return { type: 'String', value: '' }
  }
  if (typeof val === 'boolean') {
    return { type: 'Boolean', value: val }
  }
  if (typeof val === 'number' && Number.isFinite(val)) {
    return { type: Number.isInteger(val) ? 'Long' : 'Double', value: val }
  }
  return { type: 'String', value: String(val) }
}

const checkboxRowHasJacksonTypedFields = (row: unknown): boolean => {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return false
  const f = (row as { fields?: unknown }).fields
  if (!f || typeof f !== 'object' || Array.isArray(f)) return false
  const entries = Object.values(f as Record<string, unknown>)
  if (entries.length === 0) return false
  return entries.every(
    (cell) =>
      cell &&
      typeof cell === 'object' &&
      !Array.isArray(cell) &&
      typeof (cell as { type?: unknown }).type === 'string' &&
      'value' in (cell as object),
  )
}

const normalizeCheckboxUniDataTableRowForWire = (
  row: unknown,
): { fields: Record<string, { type: string; value: unknown }> } => {
  if (checkboxRowHasJacksonTypedFields(row)) {
    return row as { fields: Record<string, { type: string; value: unknown }> }
  }
  const r = row as Record<string, unknown>
  if (r?.fields && typeof r.fields === 'object' && !Array.isArray(r.fields)) {
    const out: Record<string, { type: string; value: unknown }> = {}
    for (const [k, cell] of Object.entries(r.fields as Record<string, unknown>)) {
      if (
        cell &&
        typeof cell === 'object' &&
        !Array.isArray(cell) &&
        typeof (cell as { type?: unknown }).type === 'string' &&
        'value' in (cell as object)
      ) {
        out[k] = cell as { type: string; value: unknown }
      } else {
        out[k] = checkboxCellToTyped(cell, k === 'value')
      }
    }
    return { fields: out }
  }
  return {
    fields: {
      id: checkboxCellToTyped(r?.id ?? '', false),
      description: checkboxCellToTyped(r?.description ?? '', false),
      value: checkboxCellToTyped(r?.value, true),
    },
  }
}

const normalizeCheckboxUniDataTableForWire = (table: unknown): unknown => {
  if (table == null || typeof table !== 'object' || Array.isArray(table)) return table
  const t = table as { rows?: unknown[]; columns?: unknown }
  if (!Array.isArray(t.rows) || t.rows.length === 0) return table
  return {
    ...t,
    columns:
      Array.isArray(t.columns) && t.columns.length > 0
        ? t.columns
        : ['id', 'description', 'value'],
    rows: t.rows.map((row) => normalizeCheckboxUniDataTableRowForWire(row)),
  }
}

const getCheckboxItemsFromRegisteredElement = (
  reg: unknown,
): Array<{ id: string; label: string }> => {
  const rawItems = (reg as { items?: { item?: unknown } })?.items?.item ?? (reg as { items?: unknown })?.items ?? []
  const items = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : []
  return items
    .map((item: unknown) => {
      const row = item as Record<string, { _text?: string } | undefined>
      return {
        id: String(row?.id?._text || row?.value?._text || ''),
        label: String(
          row?.description?._text || row?.name?._text || row?.value?._text || row?.id?._text || '',
        ),
      }
    })
    .filter((item) => item.id && item.label)
}

const checkboxRowHasNonEmptyId = (row: unknown): boolean => {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return false
  const r = row as Record<string, unknown>
  const fields = r?.fields as Record<string, { value?: unknown } | unknown> | undefined
  const id = fields?.id && typeof fields.id === 'object' && 'value' in fields.id
    ? fields.id.value
    : fields?.id ?? (r?.id as { value?: unknown })?.value ?? r?.id
  return id != null && String(id).trim() !== ''
}

const isUsableCheckboxTableModel = (plain: unknown, expectedItemCount: number): boolean => {
  if (plain == null || typeof plain !== 'object' || Array.isArray(plain)) return false
  const rows = (plain as { rows?: unknown }).rows
  if (!Array.isArray(rows) || rows.length === 0) return expectedItemCount === 0
  if (expectedItemCount > 0 && rows.length !== expectedItemCount) return false
  return rows.some((row) => checkboxRowHasNonEmptyId(row))
}

const buildDefaultCheckboxWireTableFromItems = (items: Array<{ id: string; label: string }>) => ({
  columns: ['id', 'description', 'value'],
  rows: items.map((opt) => ({
    fields: {
      id: checkboxCellToTyped(opt.id, false),
      description: checkboxCellToTyped(opt.label, false),
      value: checkboxCellToTyped(false, true),
    },
  })),
  empty: items.length === 0,
})

/**
 * Нормализация collectedData для getData/getFullData/putFullData (Swing parity).
 * Мутирует и возвращает тот же объект.
 */
export function normalizeElementCollectedDataForExport(
  collectedData: Record<string, unknown>,
  options: NormalizeElementCollectedDataOptions,
): Record<string, unknown> {
  const elementName = String(options.elementName ?? '').trim()
  const elTypeNorm = String(options.elTypeNorm ?? '').trim().toUpperCase()
  const registered = options.registered
  const fromElementSlot = options.fromElementSlot ?? {}
  const pageMerged = options.pageMerged ?? {}

  if (elTypeNorm === 'FILEATTACHMENTS' && !options.fileAttachmentsFromHook) {
    const rawAttachments = collectedData[elementName]
    const items = uniDataTableToFileItems(rawAttachments)
    collectedData[elementName] = fileItemsToUniDataTable(items)
    collectedData[IKey.COMPLEX_ELEMENT_NAME] = {
      type: 'String',
      value: elementName,
    }
  }

  if (elTypeNorm === 'DATERANGE') {
    const preferSlot = fromElementSlot[elementName]
    const rawDr =
      preferSlot !== undefined && preferSlot !== null ? preferSlot : collectedData[elementName]
    const dr = parseDaterangePlainForGetData(rawDr)
    collectedData[`${elementName}__first`] = formatDateRangePartForGetData(dr.from)
    collectedData[`${elementName}__second`] = formatDateRangePartForGetData(dr.to)
    delete collectedData[elementName]
  }

  if (elTypeNorm === 'CHECKBOX' || elTypeNorm === 'BOOLEAN') {
    const slotEntry = fromElementSlot[elementName]
    const plain = unwrapTypedSlotValue(slotEntry)
    if (isCheckboxLikeSlotModel(plain)) {
      collectedData[elementName] = slotEntry
    }
  }

  if (elTypeNorm === 'CHECKBOX') {
    const items = getCheckboxItemsFromRegisteredElement(registered)
    const plainCb = unwrapTypedSlotValue(collectedData[elementName])
    const usable = isUsableCheckboxTableModel(plainCb, items.length)
    if (items.length > 0 && !usable) {
      collectedData[elementName] = buildDefaultCheckboxWireTableFromItems(items)
    } else if (
      plainCb &&
      typeof plainCb === 'object' &&
      Array.isArray((plainCb as { rows?: unknown }).rows) &&
      (plainCb as { rows: unknown[] }).rows.length > 0
    ) {
      collectedData[elementName] = normalizeCheckboxUniDataTableForWire(plainCb)
    }
  } else if (elTypeNorm === 'BOOLEAN') {
    const plainCb = unwrapTypedSlotValue(collectedData[elementName])
    if (
      plainCb &&
      typeof plainCb === 'object' &&
      Array.isArray((plainCb as { rows?: unknown }).rows) &&
      (plainCb as { rows: unknown[] }).rows.length > 0
    ) {
      collectedData[elementName] = normalizeCheckboxUniDataTableForWire(plainCb)
    }
  }

  if (elTypeNorm === 'LISTBOX' || elTypeNorm === 'COMBOBOX') {
    const slotLb = fromElementSlot[elementName]
    const rawChosen =
      slotLb !== undefined && slotLb !== null ? slotLb : collectedData[elementName]
    const listboxRaw = collectedData[`${elementName}__listbox`]
    const lb = unwrapTypedSlotValue(listboxRaw)
    const rows = (lb as { rows?: unknown[] } | null)?.rows
    const comboboxIdPairs =
      elTypeNorm === 'COMBOBOX' ? parseComboBoxIdFieldPairsFromElement(registered) : []
    const rawSelected = unwrapListboxComboSelectedValueForGetData(rawChosen)
    const selectedId = String(rawSelected)

    if (elTypeNorm === 'COMBOBOX' && comboboxIdPairs.length > 0) {
      delete collectedData[`${elementName}__text`]
    }

    const selectedItem =
      Array.isArray(rows) && rows.length
        ? findListboxRowMatchingSelectedValue(rows, selectedId)
        : undefined

    if (elTypeNorm === 'LISTBOX') {
      if (selectedItem) {
        collectedData[`${elementName}__text`] = String(
          (selectedItem as { fields?: { name?: { value?: unknown }; description?: { value?: unknown } } })
            ?.fields?.name?.value ??
            (selectedItem as { fields?: { description?: { value?: unknown } } })?.fields?.description
              ?.value ??
            '',
        )
      }
    } else if (comboboxIdPairs.length > 0) {
      if (selectedItem) {
        for (const { outKey, listboxColumn } of comboboxIdPairs) {
          const typed = extractTypedFieldFromListboxRow(selectedItem, listboxColumn)
          collectedData[outKey] = typed.value
        }
      } else {
        for (const { outKey } of comboboxIdPairs) {
          collectedData[outKey] = ''
        }
      }
    } else if (selectedItem) {
      collectedData[`${elementName}__text`] = String(
        (selectedItem as { fields?: { name?: { value?: unknown }; description?: { value?: unknown } } })
          ?.fields?.name?.value ??
          (selectedItem as { fields?: { description?: { value?: unknown } } })?.fields?.description
            ?.value ??
          '',
      )
    }

    for (const suffix of ['__listbox', '__items', '__action']) {
      delete collectedData[`${elementName}${suffix}`]
    }

    if (elTypeNorm === 'LISTBOX') {
      if (selectedItem) {
        collectedData[elementName] = resolveListboxSelectionTypedValue(selectedItem).value
      } else {
        collectedData[elementName] = rawSelected === null || rawSelected === undefined ? '' : rawSelected
      }
    } else if (comboboxIdPairs.length > 0) {
      const outKeys = new Set(comboboxIdPairs.map((p) => p.outKey))
      if (!outKeys.has(elementName)) {
        delete collectedData[elementName]
      }
    } else {
      collectedData[elementName] = rawSelected
    }
  }

  if (elTypeNorm === 'MULTISELECT') {
    const slotMs = fromElementSlot[elementName]
    const rawMs =
      slotMs !== undefined && slotMs !== null ? slotMs : collectedData[elementName]
    collectedData[elementName] = toMultiSelectTypedValue(rawMs)
    for (const suffix of ['__listbox', '__items', '__action']) {
      delete collectedData[`${elementName}${suffix}`]
    }
  }

  if (isTreeTableElementType(elTypeNorm) && registered) {
    const reg = registered as {
      buildTreeTableGetDataExportContext?: () => {
        fieldName?: string
        columns?: TreeTableGetDataColumn[]
        defaultExpandByDefault?: boolean
      }
      treeTableDefaultExpandByDefault?: boolean
    }
    const ctx =
      typeof reg.buildTreeTableGetDataExportContext === 'function'
        ? reg.buildTreeTableGetDataExportContext()
        : null
    const backing = extractUniDataTableRows(collectedData[elementName])
    const columns: TreeTableGetDataColumn[] =
      ctx?.columns?.length && ctx.columns.length > 0
        ? ctx.columns
        : parseTreeTableRuntimeColumnsFromPage(pageMerged, elementName)
    const rebuilt = rebuildTreeTableGetDataForWs(ctx?.fieldName || elementName, backing, columns, {
      addAll: true,
      defaultExpandByDefault:
        ctx?.defaultExpandByDefault === true || reg.treeTableDefaultExpandByDefault === true,
    })
    if (rebuilt !== undefined) {
      collectedData[elementName] = rebuilt
    }
  }

  return collectedData
}

type GetFormSliceFn = (params: {
  formId?: string | number
  panelId?: string | number
  elementId?: string | number
  flatten?: boolean
}) => Record<string, unknown>

const resolveCheckboxRawForClientData = (
  reg: Record<string, unknown>,
  clientDataFields: Record<string, unknown>,
  mergedFlatParentData: Record<string, unknown>,
  getForm?: GetFormSliceFn,
): unknown => {
  const fieldName = String(reg.fieldName ?? '').trim()
  if (!fieldName) return undefined

  if (typeof reg.getLiveFieldValue === 'function') {
    try {
      const live = reg.getLiveFieldValue()
      if (live !== undefined && live !== null) {
        return live
      }
    } catch {
      /* fall through */
    }
  }

  if (getForm && reg.formId != null && reg.panelId != null && reg.elementId != null) {
    const slot = getForm({
      formId: reg.formId as string | number,
      panelId: reg.panelId as string | number,
      elementId: reg.elementId as string | number,
      flatten: true,
    })
    const fromSlot = slot?.[fieldName]
    if (fromSlot !== undefined && fromSlot !== null) {
      return fromSlot
    }
  }

  if (clientDataFields[fieldName] !== undefined) {
    return clientDataFields[fieldName]
  }
  return mergedFlatParentData[fieldName]
}

/**
 * Подставляет актуальное значение CheckBox/Boolean в clientData перед executeScript:
 * middle ожидает UniDataTable с typed fields, а не пустые row-shell из pagesData.
 */
export function applyCheckboxToClientDataFieldsFromRegistry(
  clientDataFields: Record<string, any>,
  mergedFlatParentData: Record<string, any>,
  elementsRegistry: Record<string, any> | null | undefined,
  getForm?: GetFormSliceFn,
): void {
  if (!elementsRegistry || typeof elementsRegistry !== 'object') return

  for (const reg of Object.values(elementsRegistry)) {
    if (!reg || typeof reg !== 'object') continue
    const elType = String(reg?.elementType ?? normalizeFormElementType(reg) ?? '').toUpperCase()
    if (elType !== 'CHECKBOX' && elType !== 'BOOLEAN') continue

    const fieldName = String(reg.fieldName ?? '').trim()
    if (!fieldName) continue

    const raw = resolveCheckboxRawForClientData(
      reg as Record<string, unknown>,
      clientDataFields,
      mergedFlatParentData,
      getForm,
    )
    if (raw === undefined) continue

    const collected: Record<string, unknown> = { [fieldName]: raw }
    normalizeElementCollectedDataForExport(collected, {
      elementName: fieldName,
      elTypeNorm: elType,
      registered: reg,
      fromElementSlot: collected,
      pageMerged: {},
    })

    const normalized = collected[fieldName]
    if (normalized === undefined || normalized === null) continue

    clientDataFields[fieldName] =
      normalized &&
      typeof normalized === 'object' &&
      !Array.isArray(normalized) &&
      'type' in normalized &&
      'value' in normalized
        ? normalized
        : setType(normalized)
  }
}

/**
 * Найти сырое значение DateRange для экспорта в wire.
 *
 * Приоритет:
 * 1. live-значение виджета (`getLiveFieldValue` → обычно `{ from, to }`)
 * 2. слот элемента через `getForm`
 * 3. поле `name` в clientData / parent snapshot
 * 4. уже развёрнутые `name__first` / `name__second` → собрать обратно в `{ from, to }`
 *
 * Возвращает `undefined`, если диапазон нигде не найден (тогда apply пропускает элемент).
 */
const resolveDateRangeRawForClientData = (
  reg: Record<string, unknown>,
  clientDataFields: Record<string, unknown>,
  mergedFlatParentData: Record<string, unknown>,
  getForm?: GetFormSliceFn,
): unknown => {
  const fieldName = String(reg.fieldName ?? '').trim()
  if (!fieldName) return undefined

  // 1) Живое значение виджета (MDateRange → { from, to }).
  if (typeof reg.getLiveFieldValue === 'function') {
    try {
      const live = (reg.getLiveFieldValue as () => unknown)()
      if (live !== undefined && live !== null) {
        return live
      }
    } catch {
      /* fall through */
    }
  }

  // 2) Слот элемента в registry/getForm.
  if (getForm && reg.formId != null && reg.panelId != null && reg.elementId != null) {
    const slot = getForm({
      formId: reg.formId as string | number,
      panelId: reg.panelId as string | number,
      elementId: reg.elementId as string | number,
      flatten: true,
    })
    const fromSlot = slot?.[fieldName]
    if (fromSlot !== undefined && fromSlot !== null) {
      return fromSlot
    }
  }

  // 3) Уже собранные поля clientData / parent snapshot.
  if (clientDataFields[fieldName] !== undefined) {
    return clientDataFields[fieldName]
  }
  if (mergedFlatParentData[fieldName] !== undefined) {
    return mergedFlatParentData[fieldName]
  }

  // 4) Уже развёрнутые Swing-поля: собрать {from,to} для повторной нормализации в DD.MM.YYYY.
  const firstKey = `${fieldName}__first`
  const secondKey = `${fieldName}__second`
  const first =
    clientDataFields[firstKey] !== undefined
      ? clientDataFields[firstKey]
      : mergedFlatParentData[firstKey]
  const second =
    clientDataFields[secondKey] !== undefined
      ? clientDataFields[secondKey]
      : mergedFlatParentData[secondKey]
  if (first === undefined && second === undefined) return undefined
  return {
    from: unwrapTypedSlotValue(first),
    to: unwrapTypedSlotValue(second),
  }
}

/** Привести значение к typed wire-полю `{ type, value }` для currentData/clientData. */
const toTypedWireString = (value: unknown): { type: string; value: unknown } => {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'type' in value &&
    'value' in value
  ) {
    return value as { type: string; value: unknown }
  }
  return setType(value == null ? '' : value)
}

/**
 * SwingDateRange → wire-формат для form/reload/action.
 *
 * UI хранит диапазон как `{ from, to }`, но Swing/`getFieldAsDate` ждут отдельные поля:
 * `{name}__first` и `{name}__second` (строки DD.MM.YYYY или пустые).
 *
 * Без разворота `createSafeClientData` / `createMinimalClientData` отбрасывают объект
 * `{from,to}` как non-primitive — фильтр по дате не доезжает до middleware
 * (см. форму 2565, `filter.regDateRange`).
 *
 * Вызывать рядом с checkbox/FA apply на путях:
 * En.putFormData, JSContext putFormData, action createClientData, useElement.reload.
 */
export function applyDateRangeToClientDataFieldsFromRegistry(
  clientDataFields: Record<string, any>,
  mergedFlatParentData: Record<string, any>,
  elementsRegistry: Record<string, any> | null | undefined,
  getForm?: GetFormSliceFn,
): void {
  if (!elementsRegistry || typeof elementsRegistry !== 'object') return

  for (const reg of Object.values(elementsRegistry)) {
    if (!reg || typeof reg !== 'object') continue
    const elType = String(reg?.elementType ?? normalizeFormElementType(reg) ?? '').toUpperCase()
    if (elType !== 'DATERANGE') continue

    const fieldName = String(reg.fieldName ?? '').trim()
    if (!fieldName) continue

    const raw = resolveDateRangeRawForClientData(
      reg as Record<string, unknown>,
      clientDataFields,
      mergedFlatParentData,
      getForm,
    )
    if (raw === undefined) continue

    // Переиспользуем element getData-экспорт: delete name, set name__first/__second.
    const collected: Record<string, unknown> = { [fieldName]: raw }
    normalizeElementCollectedDataForExport(collected, {
      elementName: fieldName,
      elTypeNorm: 'DATERANGE',
      registered: reg,
      fromElementSlot: collected,
      pageMerged: {},
    })

    const firstKey = `${fieldName}__first`
    const secondKey = `${fieldName}__second`
    if (collected[firstKey] !== undefined) {
      clientDataFields[firstKey] = toTypedWireString(collected[firstKey])
    }
    if (collected[secondKey] !== undefined) {
      clientDataFields[secondKey] = toTypedWireString(collected[secondKey])
    }
    // UI-ключ `{from,to}` на wire не отправляем.
    delete clientDataFields[fieldName]
  }
}
